import {
    DropDown,
    FileDownload,
    getFilterElementsDefault,
    sharpDateFormatter,
    useErrorState,
    useWorkplaceState,
    WorkplaceBtnActions
} from '@finbeat/react-toolkit';
import { faBoltLightning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getSelectList } from 'axios/dictionaryApi';
import { addReport, downloadReport, getReportsExcel, getReportsList } from 'axios/reportApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import DataTimeText from 'components/Common/DataTime';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { ReportAdd, ReportFilter as ReportFilterType, ReportTable } from 'models/api/report';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FaDownload } from 'react-icons/fa';
import { useQuery, useQueryClient } from 'react-query';
import { getEndOfTheDay, getStartOfTheDay } from 'utils/dataTime';
import ReportAddButton from './ReportAddButton';
import ReportsChangeModal from './ReportChangeModal';
import ReportDetailsButton from './ReportDetailsButton';
import ReportReloadButton from './ReportReloadButton';
import ReportFilter from './ReportsFilter';
import ReportUpdater from './ReportUpdater';

const Reports = () => {
    const { t, i18n } = useTranslation();
    const api = useApi();
    const queryClient = useQueryClient();
    const [isModalVisible, setModalVisible] = useState(false);
    const { dateTimeFormat } = useGeneralSettingsContext();
    const { hasRole } = useAuth();
    const { handleRequestError } = useErrorState();
    const [reportTypes, setReportTypes] = useState<DropDown[]>([]);

    const { isFetching: isReportTypesFetching, refetch: refetchReportTypes } = useQuery(
        'reportTypes',
        () => getSelectList(api, ['ReportTypes'], null, true),
        {
            onSuccess: (data) => {
                setReportTypes(data['ReportTypes'] || []);
            },
            onError: (err) => handleRequestError(err),
            enabled: !reportTypes.length,
        }
    );

    useEffect(() => {
        refetchReportTypes();
    }, [i18n.language]);

    return (
        <BOWorkplace<ReportTable, ReportTable, ReportFilterType, ReportTable, ReportAdd>
            pageTitle="BackOfficeReports"
            filterParams={{
                sd: getStartOfTheDay(),
                ed: getEndOfTheDay(),
            }}
            initColumns={[
                {
                    key: 'id',
                    name: <Trans i18nKey="BackOfficeId" />,
                    width: 70,
                    minWidth: 70,
                },
                { key: 'type', name: <Trans i18nKey="BackOfficeReportType" /> },
                { key: 'status', name: <Trans i18nKey="BackOfficeStatus" /> },
                { key: 'userId', name: <Trans i18nKey="BackOfficeOperator" /> },
                {
                    key: 'createDate',
                    name: <Trans i18nKey="BackOfficeCreateDate" />,
                    formatter: ({ row }) => <DataTimeText value={row.createDate} />,
                },
                {
                    key: 'startDate',
                    name: <Trans i18nKey="BackOfficeStartDate" />,
                    formatter: ({ row }) => <DataTimeText value={row.startDate} />,
                },
                {
                    key: 'endDate',
                    name: <Trans i18nKey="BackOfficeEndDate" />,
                    formatter: ({ row }) => <DataTimeText value={row.endDate} />,
                },
                { key: 'errMsg', name: <Trans i18nKey="BackOfficeErrorMessage" /> },
                {
                    key: 'actions',
                    name: <FontAwesomeIcon icon={faBoltLightning} />,
                    cellClass: 'base-data-grid__cell_centered no-change-modal',
                    headerCellClass: 'base-data-grid__cell_centered',
                    width: 70,
                    minWidth: 70,
                    draggable: false,
                    resizable: false,
                    clickDisable: true,
                    formatter: ({ row }) => {
                        if (row.systemStatus === 'Success') {
                            return (
                                <WorkplaceBtnActions>
                                    {row.systemType !== 'Monitoring' &&
                                        hasRole('reportsGetDetails') && (
                                            <ReportDetailsButton id={row.id} key="0" />
                                        )}
                                    {hasRole('reportsGetDetailsExcel') && (
                                        <FileDownload
                                            className="text-body text-decoration-none"
                                            fileName={`${row.systemType}#${row.id}.xlsx`}
                                            mutationKey={`${row.systemType}_${row.id}`}
                                            query={async () =>
                                                (await downloadReport(api, row.id, undefined)).data
                                            }
                                        >
                                            <div className="workplace-action-btn cursor--pointer">
                                                <FaDownload className="text-success workplace-action-btn__icon" />
                                                <span>
                                                    <Trans i18nKey="BackOfficeToExcel" />
                                                </span>
                                            </div>
                                        </FileDownload>
                                    )}
                                </WorkplaceBtnActions>
                            );
                        } else if (row.systemStatus === 'Del') {
                            return (
                                <WorkplaceBtnActions>
                                    {hasRole('reportsReload') && (
                                        <ReportReloadButton id={row.id} key="2" />
                                    )}
                                </WorkplaceBtnActions>
                            );
                        }

                        return <></>;
                    },
                },
            ]}
            storagePath="reports/columns"
            getFilterElements={getFilterElementsDefault([
                {
                    id: 'sd',
                    name: t('BackOfficeStartDate'),
                    formatter: (val) => sharpDateFormatter(val, dateTimeFormat) || '',
                },
                {
                    id: 'ed',
                    name: t('BackOfficeEndDate'),
                    formatter: (val) => sharpDateFormatter(val, dateTimeFormat) || '',
                },
                {
                    id: 'type',
                    name: t('BackOfficeReportType'),
                    formatter: (val) =>
                        reportTypes.find((t) => t.id?.toString() === val)?.name || '',
                },
            ])}
            loadingFilter={isReportTypesFetching}
            showModal={hasRole('propsGet') ? () => setModalVisible(true) : undefined}
            buttons={
                hasRole('reportsCreate')
                    ? [<ReportAddButton key="1" showModal={() => setModalVisible(true)} />]
                    : []
            }
            filter={<ReportFilter reportTypes={reportTypes} />}
            modal={{
                component: isModalVisible && (
                    <ReportsChangeModal
                        isOpen={isModalVisible}
                        toggle={setModalVisible}
                        reportTypes={reportTypes}
                    />
                ),
                addMutationFn: (item) => addReport(api, item),
            }}
            fetchQueryFn={(filter) => getReportsList(api, filter)}
            queryKey={['reports']}
            queryClient={queryClient}
            fetchExcelQueryFn={(filter) => getReportsExcel(api, filter)}
            rowClass={(row) => (row.errMsg ? 'bg-lingt-danger bg-gradient' : undefined)}
            showExcelBtn={hasRole('reportsExcel')}
            refetchOnLanguageChange={true}
        >
            <ReportUpdater />
        </BOWorkplace>
    );
};

const useReportWorkplaceState = () =>
    useWorkplaceState<ReportTable, ReportTable, ReportTable, ReportAdd>();

export default Reports;
export { useReportWorkplaceState };
